import React, { useMemo } from 'react';
import {
  isMetaAnswerKey,
  isPngFileName,
  normalizeFileAnswerValueForKey,
  prettyAnswerKey,
  uploadedFilesFromAnswers,
} from '../../../features/flow/utils';

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '' || v.trim().toLowerCase() === 'no' || v.trim().toLowerCase() === 'none';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function isDateLikeKey(key: string) {
  return /(date|dob|birth|acquisition)/i.test(key);
}

function isIsoDateString(v: unknown) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
}

function isLikelyUploadKey(key: string) {
  return key.toLowerCase().includes('upload');
}

function fileGroupLabel(answerKey: string): string {
  const inv = answerKey.match(/^investment_(\d+)_upload_(.+)$/);
  if (inv) {
    const idx = inv[1];
    const rest = inv[2];
    return `Investment ${idx} ${prettyAnswerKey(rest)}`;
  }
  const ent = answerKey.match(/^entity_(\d+)_upload$/);
  if (ent) return `Foreign entity ${ent[1]}`;
  if (answerKey === 'entity_upload') return 'Foreign entity';
  const asset = answerKey.match(/^asset_(\d+)_upload_(.+)$/);
  if (asset) return `Asset ${asset[1]} ${prettyAnswerKey(asset[2])}`;
  const crypto = answerKey.match(/^crypto_(\d+)_upload_(.+)$/);
  if (crypto) return `Crypto ${crypto[1]} ${prettyAnswerKey(crypto[2])}`;
  return prettyAnswerKey(answerKey);
}

function sectionTitleForKey(key: string): string {
  if (/^bank_account_\d+_/.test(key)) return 'Foreign bank accounts';
  if (/^investment_\d+_/.test(key)) return 'Foreign investments';
  if (/^(entity_\d+_|entity_)?/.test(key) || /^entity_\d+_/.test(key) || key.startsWith('entity_')) return 'Foreign companies / trusts';
  if (/^asset_\d+_/.test(key)) return 'Foreign property / assets';
  if (/^crypto_\d+_/.test(key)) return 'Foreign crypto holdings';
  return 'Foreign reporting';
}

type Row = { key: string; label: string };

export function ForeignReportingReviewScreenBody({
  answers,
  actionBusy,
  setAnswers,
}: {
  answers: Record<string, unknown>;
  actionBusy: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const sections = useMemo(() => {
    const keys = Object.keys(answers ?? {}).filter((k) => !isMetaAnswerKey(k) && !isLikelyUploadKey(k));

    const take = (predicate: (k: string) => boolean): Row[] => {
      return keys
        .filter(predicate)
        .sort()
        .filter((k) => !isEmptyValue(answers[k]))
        .map((k) => ({ key: k, label: prettyAnswerKey(k) }));
    };

    const foreignBankAccounts = take((k) => /^bank_account_\d+_/.test(k));
    const foreignInvestments = take((k) => /^investment_\d+_/.test(k));
    const foreignEntities = take((k) => /^entity_\d+_/.test(k) || k.startsWith('entity_'));
    const foreignCrypto = take((k) => /^crypto_\d+_/.test(k));

    return [
      { title: 'Foreign bank accounts', rows: foreignBankAccounts },
      { title: 'Foreign investments', rows: foreignInvestments },
      { title: 'Foreign companies / trusts', rows: foreignEntities },
      { title: 'Foreign crypto holdings', rows: foreignCrypto },
    ].filter((s) => s.rows.length > 0);
  }, [answers]);

  const uploadedFilesForReview = useMemo(() => {
    return uploadedFilesFromAnswers(answers)
      .filter((row) => {
        const k = row.answerKey;
        if (/broker_report/i.test(k)) return false;
        return (
          /^investment_\d+_upload/.test(k) ||
          k === 'entity_upload' ||
          /^entity_\d+_upload/.test(k) ||
          /^crypto_\d+_upload/.test(k)
        );
      })
      .map((row) => ({ ...row, groupLabel: fileGroupLabel(row.answerKey), sectionName: sectionTitleForKey(row.answerKey) }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, [answers]);

  return (
    <div className="w-full flex flex-col gap-10">
      {sections.map((sec, idx) => (
        <div key={sec.title} className="w-full">
          {idx > 0 ? <div className="w-full border-t border-[#E2E8F0] pt-10" /> : null}
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-base font-medium text-[#131315]">{sec.title}:</div>
          </div>

          <div className="mt-6 w-full max-w-[668px] grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 items-start">
            {sec.rows.map(({ label, key }) => {
              const v = answers[key];
              const valStr = v === null || v === undefined ? '' : String(v);
              const isNum = typeof v === 'number';
              const isArr = Array.isArray(v);
              const isDate = isIsoDateString(v) || isDateLikeKey(key);

              return (
                <div key={key} className="min-w-0 col-span-1">
                  <div className="flex flex-col gap-3">
                    <label className="block text-sm text-[#131315] font-medium">{label}</label>

                    {isArr ? (
                      <textarea
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        value={JSON.stringify(v)}
                        disabled={actionBusy}
                        onChange={(e) => {
                          const raw = e.target.value;
                          try {
                            const parsed = JSON.parse(raw);
                            setAnswers((prev) => ({ ...prev, [key]: parsed }));
                          } catch {
                            setAnswers((prev) => ({ ...prev, [key]: raw }));
                          }
                        }}
                        rows={3}
                      />
                    ) : (
                      <input
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        type={isDate ? 'date' : isNum ? 'number' : 'text'}
                        value={valStr}
                        disabled={actionBusy}
                        onChange={(e) =>
                          setAnswers((prev) => ({
                            ...prev,
                            [key]: isNum ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {uploadedFilesForReview.length > 0 ? (
        <div className="w-full border-t border-[#E2E8F0] pt-10">
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-sm font-semibold text-[#131315]">Uploaded Files:</div>
          </div>

          <div className="mt-4 w-full max-w-[668px] flex flex-col gap-3">
            {(() => {
              const seen = new Map<string, number>();
              return uploadedFilesForReview.map((row) => {
                const next = (seen.get(row.groupLabel) ?? 0) + 1;
                seen.set(row.groupLabel, next);
                return (
                  <div key={`${row.answerKey}_${row.fileName}_${row.fileIndex}`}>
                    <div className="text-xs text-[#525353] mb-2">{row.groupLabel}</div>
                    <div className="w-full max-w-[310px] rounded-lg border border-[#E2E8F0] bg-[#F9FAFB] px-4 py-3 flex items-center justify-between">
                      <span className="text-sm text-[#131315] break-all">
                        <span className="block text-base font-medium text-[#131315] mb-1">
                          File {next}: {row.fileName}
                        </span>
                      </span>
                      <button
                        type="button"
                        disabled={actionBusy}
                        onClick={() => {
                          setAnswers((prev) => {
                            const curVal = prev[row.answerKey];
                            const curAll = normalizeFileAnswerValueForKey(row.answerKey, curVal);
                            let pngSeen = -1;
                            const nextAll = curAll.filter((ref) => {
                              if (!isPngFileName(ref.name)) return true;
                              pngSeen += 1;
                              if (pngSeen === row.fileIndex && ref.name === row.fileName) return false;
                              return true;
                            });
                            return { ...prev, [row.answerKey]: nextAll.length > 0 ? nextAll : null };
                          });
                        }}
                        className="text-base font-medium text-[#131315] disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
